import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Hand, X } from 'lucide-react';
import { useQueue } from '../../context/QueueContext';
import { statusColor, statusLabel, timeAgo } from '../../data/mockRequests';
import { getUser } from '../../data/users';
import { formatBRL } from '../../data/catalog';
import { listReviewItems } from '../../utils/review';
import { VARIANT_STATUS } from '../../utils/availability';
import { listPendingPhotos, usePhotoTick } from '../../utils/photoStatus';

export function StatusBadge({ status }) {
  return (
    <span
      className="inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold text-white"
      style={{ backgroundColor: statusColor(status) }}
      data-testid={`status-badge-${status}`}
    >
      {statusLabel(status)}
    </span>
  );
}

export const formatDate = (iso) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4" data-testid={testid}>
      <p className="font-serif text-2xl text-navy">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { requests, user, acceptRequest } = useQueue();
  usePhotoTick();
  const [q, setQ] = useState('');
  const [notice, setNotice] = useState(null);
  const isAdmin = user.role === 'admin';

  const queue = requests
    .filter((r) => !r.assignedTo && r.status === 'aguardando_atendimento')
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const mine = requests.filter((r) => r.assignedTo === user.id);
  const mineActive = mine.filter((r) => !['concluida', 'cancelada'].includes(r.status));

  const handleAccept = async (id) => {
    const res = await acceptRequest(id);
    if (!res.ok) {
      setNotice(
        res.reason === 'limite'
          ? 'Você já tem 4 atendimentos abertos. Conclua ou aguarde a finalização de algum antes de aceitar outro.'
          : 'Esta solicitação acabou de ser assumida por outro vendedor.'
      );
    }
  };

  const adminStats = {
    aguardando: requests.filter((r) => r.status === 'aguardando_atendimento').length,
    emAtendimento: requests.filter((r) => ['em_atendimento', 'proposta_em_preparacao'].includes(r.status)).length,
    enviadas: requests.filter((r) => r.status === 'proposta_enviada').length,
    concluidas: requests.filter((r) => r.status === 'concluida').length,
    totalPropostas: requests.reduce((s, r) => s + (r.proposalValue || 0), 0),
  };

  const adminList = requests.filter(
    (r) => q === '' || r.customer.toLowerCase().includes(q.toLowerCase()) || String(r.id).includes(q)
  );

  const reviewItems = listReviewItems();
  const reviewPending = reviewItems.filter((i) => i.situation === 'PENDENTE' || i.situation === 'ATRASADA').length;
  const reviewLate = reviewItems.filter((i) => i.situation === 'ATRASADA').length;
  const reviewPostponed = reviewItems.filter((i) => i.situation === 'ADIADA').length;
  const reviewOos = reviewItems.filter((i) => i.status === VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL).length;

  const reviewAlert = (
    <Link
      to="/admin/revisao"
      data-testid="review-alert"
      className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300/70 bg-amber-50 px-5 py-4 transition-colors hover:bg-amber-100"
    >
      <div>
        <p className="text-sm font-semibold text-amber-900">Revisão de Catálogo</p>
        <p className="text-xs text-amber-800" data-testid="review-alert-counts">
          {reviewPending} {reviewPending === 1 ? 'item pendente' : 'itens pendentes'}
          {isAdmin && ` · ${reviewLate} atrasados · ${reviewPostponed} adiados · ${reviewOos} indisponíveis`}
        </p>
      </div>
      <span className="rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white">REVISAR AGORA</span>
    </Link>
  );

  const photoPending = listPendingPhotos().length;
  const photoAlert = photoPending > 0 && (
    <Link
      to="/admin/fotos"
      data-testid="photo-alert"
      className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy/15 bg-white px-5 py-4 transition-colors hover:bg-navy-light/40"
    >
      <div>
        <p className="text-sm font-semibold text-navy">Fotos das Variantes</p>
        <p className="text-xs text-muted-foreground" data-testid="photo-alert-count">
          {photoPending} {photoPending === 1 ? 'variante aguardando' : 'variantes aguardando'} foto original
        </p>
      </div>
      <span className="rounded-full border border-navy/25 px-5 py-2.5 text-xs font-semibold text-navy">VER FOTOS</span>
    </Link>
  );

  return (
    <div data-testid="admin-dashboard">
      {notice && (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800" data-testid="accept-conflict-notice" role="alert">
          {notice}
          <button onClick={() => setNotice(null)} aria-label="Fechar aviso" className="p-1"><X className="h-4 w-4" /></button>
        </div>
      )}

      {isAdmin ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="vendor-dashboard-status-filter">
            <StatCard label="Aguardando atendimento" value={adminStats.aguardando} testid="stat-aguardando" />
            <StatCard label="Em atendimento" value={adminStats.emAtendimento} testid="stat-em-atendimento" />
            <StatCard label="Propostas enviadas" value={adminStats.enviadas} testid="stat-propostas-enviadas" />
            <StatCard label="Concluídas" value={adminStats.concluidas} testid="stat-concluidas" />
            <StatCard label="Valor em propostas" value={formatBRL(adminStats.totalPropostas)} testid="stat-total-propostas" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Futuro: relatórios por vendedor, período, status, valores e tempo de atendimento.
          </p>
          {reviewAlert}
          {photoAlert}

          <div className="mt-6 flex items-center justify-between gap-4">
            <h2 className="font-serif text-xl text-navy">Todas as Solicitações</h2>
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por cliente ou número"
              aria-label="Buscar solicitação"
              className="h-11 w-full max-w-xs rounded-lg border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
            />
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white" data-testid="admin-requests-table">
            <div className="hidden grid-cols-[80px_1fr_130px_170px_150px_110px] gap-3 border-b border-navy/10 bg-navy-light px-5 py-3 text-xs font-semibold uppercase tracking-wide text-navy lg:grid">
              <span>Número</span><span>Cliente</span><span>Vendedor</span><span>Status</span><span>Data/hora</span><span>Valor</span>
            </div>
            {adminList.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground" data-testid="admin-requests-empty">Nenhuma solicitação encontrada.</p>
            )}
            {adminList.map((r) => (
              <Link
                key={r.id}
                to={`/admin/solicitacao/${r.id}`}
                data-testid={`admin-request-row-${r.id}`}
                className="grid grid-cols-2 gap-2 border-b border-navy/5 px-5 py-4 text-sm transition-colors last:border-0 hover:bg-navy-light/50 lg:grid-cols-[80px_1fr_130px_170px_150px_110px] lg:items-center lg:gap-3"
              >
                <span className="font-serif font-semibold text-navy">#{r.id}</span>
                <span className="font-medium">{r.customer}</span>
                <span className="text-muted-foreground">{r.assignedTo ? getUser(r.assignedTo)?.name : '—'}</span>
                <span><StatusBadge status={r.status} /></span>
                <span className="text-muted-foreground">{formatDate(r.date)}</span>
                <span className="font-medium">{r.proposalValue != null ? formatBRL(r.proposalValue) : '—'}</span>
              </Link>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="vendor-dashboard-status-filter">
            <StatCard label="Solicitações disponíveis" value={queue.length} testid="stat-disponiveis" />
            <StatCard label="Meus atendimentos" value={mineActive.length} testid="stat-meus-atendimentos" />
            <StatCard label="Aguardando cliente" value={mine.filter((r) => r.status === 'aguardando_cliente').length} testid="stat-aguardando-cliente" />
            <StatCard label="Propostas enviadas" value={mine.filter((r) => r.status === 'proposta_enviada').length} testid="stat-minhas-propostas" />
          </div>
          {reviewAlert}
          {photoAlert}

          <h2 className="mt-8 font-serif text-xl text-navy">Solicitações Disponíveis</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Para garantir uma distribuição neutra, os detalhes comerciais só são revelados após você aceitar o atendimento.
          </p>
          <div className="mt-4 space-y-3" data-testid="available-queue">
            {queue.length === 0 && (
              <p className="rounded-2xl border border-dashed border-navy/20 bg-white p-8 text-center text-sm text-muted-foreground" data-testid="queue-empty">
                Nenhuma solicitação aguardando atendimento no momento.
              </p>
            )}
            {queue.map((r) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy/10 bg-white px-5 py-4"
                data-testid={`queue-item-${r.id}`}
              >
                <div>
                  <p className="font-serif text-lg font-semibold text-navy">#{r.id}</p>
                  <p className="text-sm font-medium">{r.customer}</p>
                  <p className="text-xs text-muted-foreground">Recebida {timeAgo(r.date)}</p>
                </div>
                <button
                  onClick={() => handleAccept(r.id)}
                  data-testid={`queue-accept-${r.id}`}
                  className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
                >
                  <Hand className="h-4 w-4" /> ACEITAR ATENDIMENTO
                </button>
              </div>
            ))}
          </div>

          <h2 id="meus-atendimentos" className="mt-10 scroll-mt-6 font-serif text-xl text-navy" data-testid="dashboard-my-requests-title">Meus Atendimentos</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white" data-testid="my-requests-table">
            <div className="hidden grid-cols-[80px_1fr_170px_150px_110px] gap-3 border-b border-navy/10 bg-navy-light px-5 py-3 text-xs font-semibold uppercase tracking-wide text-navy md:grid">
              <span>Número</span><span>Cliente</span><span>Status</span><span>Data/hora</span><span>Proposta</span>
            </div>
            {mine.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground" data-testid="my-requests-empty">
                Você ainda não assumiu nenhum atendimento.
              </p>
            )}
            {mine.map((r) => (
              <Link
                key={r.id}
                to={`/admin/solicitacao/${r.id}`}
                data-testid={`my-request-row-${r.id}`}
                className="grid grid-cols-2 gap-2 border-b border-navy/5 px-5 py-4 text-sm transition-colors last:border-0 hover:bg-navy-light/50 md:grid-cols-[80px_1fr_170px_150px_110px] md:items-center md:gap-3"
              >
                <span className="font-serif font-semibold text-navy">#{r.id}</span>
                <span className="font-medium">{r.customer}</span>
                <span><StatusBadge status={r.status} /></span>
                <span className="text-muted-foreground">{formatDate(r.date)}</span>
                <span className="font-medium">{r.proposalValue != null ? formatBRL(r.proposalValue) : '—'}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
